/* Client for the Athos (Searchspring) category/search/recommend API.
   window.AthosClient.createClient({ siteId, ajaxCatalog }) -> { fetchCategoryPage, fetchSearchPage, fetchRecommendations }.
   Also exports normalizeProduct, normalizeField, handleFromResult, decodeHtmlEntities. */
(() => {
  if (window.AthosClient) return;

  const responseCacheLimit = 100;
  const retryDelay = 500;
  const rateLimitRetryDelay = 1800;
  const legacyFieldAliases = {
    color_family: 'mfield_jam_spec_color_family',
    dtc_sales_ranking: 'mfield_jam_spec_dtc_sales_ranking',
    envelope_closure: 'mfield_jam_spec_envelope_closure',
    envelope_size: 'mfield_jam_spec_envelope_size',
    final_price: 'price',
    has_window: 'mfield_jam_spec_has_window',
    item_primary_category: 'mfield_jam_taxonomy_primary',
    name: 'title',
    security_tint: 'mfield_jam_spec_security_tint',
  };

  const normalizeField = (field) => {
    const normalized = String(field || '').trim();
    return legacyFieldAliases[normalized] || normalized;
  };

  const createPageLoadId = () => (
    window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  const getCookie = (name) => {
    const prefix = `${encodeURIComponent(name)}=`;
    const entry = document.cookie.split('; ').find((candidate) => candidate.startsWith(prefix));
    return entry ? decodeURIComponent(entry.slice(prefix.length)) : '';
  };

  const getFirstCookie = (...names) => names.map(getCookie).find(Boolean) || '';

  /* Read the theme's localStorage `<themeName>:recently-viewed` ids for the lastViewed signal. */
  const getThemeRecentlyViewed = () => {
    try {
      const name = window.theme?.settings?.themeName;
      let raw = name ? window.localStorage.getItem(`${name}:recently-viewed`) : '';
      if (!raw) {
        for (let i = 0; i < window.localStorage.length; i += 1) {
          const key = window.localStorage.key(i);
          if (key && key.endsWith(':recently-viewed')) {
            raw = window.localStorage.getItem(key);
            break;
          }
        }
      }
      const ids = JSON.parse(raw || '[]');
      return Array.isArray(ids) ? ids.map((value) => String(value ?? '').trim()).filter(Boolean) : [];
    } catch (_error) {
      return [];
    }
  };

  const getLastViewed = () => {
    try {
      const tracked = window.athos?.tracker?.track?.viewed?.get?.();
      if (Array.isArray(tracked) && tracked.length) {
        return tracked.map((value) => String(value ?? '').trim()).filter(Boolean).slice(0, 20).join(',');
      }
    } catch (_error) {
      /* Fall through to the theme's own viewed-products list, then the cookie. */
    }
    const themeViewed = getThemeRecentlyViewed();
    if (themeViewed.length) return themeViewed.slice(0, 20).join(',');
    return getFirstCookie('athosViewedProducts', 'ssViewedProducts')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
      .slice(0, 20)
      .join(',');
  };

  const abortableDelay = (delay, signal) => new Promise((resolve, reject) => {
    const timeout = window.setTimeout(resolve, delay);
    signal?.addEventListener('abort', () => {
      window.clearTimeout(timeout);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });

  const appendRawQuery = (target, rawQuery) => {
    if (!rawQuery) return;

    let source;
    try {
      const query = rawQuery.trim();
      source = /^https?:\/\//i.test(query)
        ? new URL(query).searchParams
        : new URLSearchParams(query.replace(/^\?/, ''));
    } catch (_error) {
      return;
    }

    source.forEach((value, key) => {
      const fieldParameter = key.match(/^(bgfilter|filter)\.(.+)$/);
      if (fieldParameter) {
        target.append(`${fieldParameter[1]}.${normalizeField(fieldParameter[2])}`, value);
      } else {
        target.append(key, value);
      }
    });
  };

  /* Decode Athos labels before callers assign them through textContent. */
  const decodeHtmlEntities = (value) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(value ?? '');
    return textarea.value;
  };

  const normalizeUrl = (value) => {
    if (!value) return '';
    try {
      const url = new URL(String(value).replace(/&amp;/g, '&'), window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
    } catch (_error) {
      return '';
    }
  };

  const parsePrice = (value) => {
    const price = Number.parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(price) ? price : undefined;
  };

  const firstNonEmpty = (values) => values.find((value) => (
    Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== ''
  ));

  const normalizeProduct = (result) => {
    const core = result?.mappings?.core || {};
    const attributes = result?.attributes || {};
    const read = (...keys) => {
      const found = firstNonEmpty(keys.map((key) => core[key] ?? result?.[key] ?? attributes[key]));
      return (Array.isArray(found) ? found[0] : found) ?? '';
    };

    return {
      id: String(read('uid', 'id', 'sku')),
      name: decodeHtmlEntities(read('name', 'title', 'short_name')),
      sku: String(read('sku')),
      url: normalizeUrl(read('url')),
      imageUrl: normalizeUrl(read('imageUrl', 'thumbnailImageUrl', 'image_url')),
      price: parsePrice(read('final_price', 'price')),
      comparePrice: parsePrice(read('msrp', 'compare_at_price')),
      raw: result,
    };
  };

  /* 'field|dir' -> { field, direction } for the client. '' (Featured) -> undefined. */
  const parseSortValue = (value) => {
    const [field, direction] = String(value || '').split('|');
    return field ? { field, direction: direction === 'desc' ? 'desc' : 'asc' } : undefined;
  };

  /* The product handle for a raw Athos result. Handles both flat and nested response shapes. */
  const handleFromResult = (result) => {
    const handle = result?.attributes?.handle || result?.handle;
    if (handle) return String(handle).trim();
    const url = normalizeUrl(result?.mappings?.core?.url || result?.attributes?.url || result?.url);
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  };

  const createClient = ({ siteId, ajaxCatalog }) => {
    const site = (siteId || '').trim();
    const catalog = (ajaxCatalog || 'athos/snap/preact/templates/1.6.0').trim();
    const pageLoadId = createPageLoadId();
    const cache = new Map();

    const assertSite = () => {
      if (!/^[a-z0-9_-]+$/i.test(site)) throw new Error('A valid Athos site ID is required.');
    };

    /* Shared category/search params. Runtime keys are wiped first so a raw query can't shadow them. */
    const applyCommonParams = (url, request) => {
      const params = url.searchParams;
      ['siteId', 'ajaxCatalog', 'resultsFormat', 'resultsPerPage', 'page', 'domain', 'pageLoadId', 'userId', 'sessionId', 'lastViewed']
        .forEach((key) => params.delete(key));

      params.set('resultsFormat', 'native');
      params.set('siteId', site);
      params.set('resultsPerPage', String(request.resultsPerPage ?? 12));
      params.set('page', String(request.page ?? 1));
      params.set('domain', window.location.href);
      params.set('pageLoadId', pageLoadId);

      const userId = getFirstCookie('athosUserId', 'ssUserId');
      const sessionId = getFirstCookie('athosSessionId', 'ssSessionId');
      const lastViewed = getLastViewed();
      if (catalog) params.set('ajaxCatalog', catalog);
      if (userId) params.set('userId', userId);
      if (sessionId) params.set('sessionId', sessionId);
      if (lastViewed) params.set('lastViewed', lastViewed);

      (request.filters || []).forEach(({ field, value }) => {
        const normalized = normalizeField(field);
        if (value && normalized) params.append(`filter.${normalized}`, value);
      });

      if (request.sort?.field) {
        Array.from(new Set(params.keys()))
          .filter((key) => key.startsWith('sort.'))
          .forEach((key) => params.delete(key));
        params.set(`sort.${normalizeField(request.sort.field)}`, request.sort.direction === 'desc' ? 'desc' : 'asc');
      }

      return url;
    };

    const buildCategoryUrl = (request) => {
      assertSite();
      const url = new URL(`https://${site}.a.athoscommerce.net/v1/category`);
      const params = url.searchParams;
      const collectionId = (request.collectionId || '').trim();
      const collectionHandle = (request.collectionHandle || '').trim();
      const hasCollection = Boolean(collectionId || collectionHandle);

      appendRawQuery(params, request.rowQuery);
      if (!hasCollection) appendRawQuery(params, request.globalQuery);
      if (hasCollection) {
        params.delete('bgfilter.collection_id');
        params.delete('bgfilter.collection_handle');
        /* The index scopes by collection_handle; keep both values anyway. */
        if (collectionId) params.set('bgfilter.collection_id', collectionId);
        if (collectionHandle) params.set('bgfilter.collection_handle', collectionHandle);
      }

      return applyCommonParams(url, request);
    };

    /* /v1/search sibling of the category endpoint, scoped by q instead of a collection. */
    const buildSearchUrl = (request) => {
      assertSite();
      const url = new URL(`https://${site}.a.athoscommerce.net/v1/search`);
      const params = url.searchParams;
      appendRawQuery(params, request.rowQuery);
      appendRawQuery(params, request.globalQuery);
      params.set('q', (request.searchTerms || '').trim());
      return applyCommonParams(url, request);
    };

    const buildAutocompleteUrl = (request) => {
      assertSite();
      const url = new URL(`https://${site}.a.athoscommerce.net/v1/autocomplete`);
      const params = url.searchParams;
      appendRawQuery(params, request.rowQuery);
      appendRawQuery(params, request.globalQuery);
      params.set('q', (request.searchTerms || '').trim());
      return applyCommonParams(url, request);
    };

    const fetchPage = async (url, { signal } = {}) => {
      const cacheKey = url.toString();
      if (cache.has(cacheKey)) return cache.get(cacheKey);

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await fetch(url, {
            method: 'GET',
            headers: { Accept: 'application/json' },
            credentials: 'omit',
            signal,
          });

          if (response.status === 429) {
            /* Back off well past the throttle window before the one retry. */
            const error = new Error('Athos request failed with 429');
            error.retryDelay = rateLimitRetryDelay;
            throw error;
          }
          if (!response.ok) throw new Error(`Athos request failed with ${response.status}`);

          const data = await response.json();
          /* Add a products-only count at the API boundary; Athos's slot-based fields stay untouched. */
          if (data?.pagination) data.pagination.productResults = productResultCount(data);
          cache.set(cacheKey, data);
          if (cache.size > responseCacheLimit) cache.delete(cache.keys().next().value);
          return data;
        } catch (error) {
          if (error?.name === 'AbortError' || attempt > 0) throw error;
          await abortableDelay(error?.retryDelay || retryDelay, signal);
        }
      }

      return undefined;
    };

    const fetchCategoryPage = (request, options) => fetchPage(buildCategoryUrl(request), options);
    const fetchSearchPage = (request, options) => fetchPage(buildSearchUrl(request), options);
    const fetchAutocomplete = (request, options) => fetchPage(buildAutocompleteUrl(request), options);

    const fetchSuggest = async (query, { signal, limit = 6 } = {}) => {
      assertSite();
      const term = String(query || '').trim();
      if (!term) return [];
      const url = new URL(`https://${site}.a.athoscommerce.net/v1/suggest`);
      url.searchParams.set('siteId', site);
      url.searchParams.set('language', 'en');
      url.searchParams.set('query', term);
      url.searchParams.set('suggestionCount', String(limit));

      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'omit',
        signal,
      });
      if (!response.ok) throw new Error(`Athos suggest failed with ${response.status}`);

      const data = await response.json();
      const alternatives = Array.isArray(data?.alternatives) ? data.alternatives.map((entry) => entry?.text) : [];
      return Array.from(new Set([data?.suggested?.text, ...alternatives]
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)));
    };

    /* POST the profile tag and product context to the p13n host; return recommended handles. */
    const fetchRecommendations = async ({ profileTag, limit = 6, productId, lastViewed } = {}, { signal } = {}) => {
      if (!/^[a-z0-9_-]+$/i.test(site)) throw new Error('A valid Athos site ID is required.');

      const body = {
        profiles: [{ tag: String(profileTag || 'similar').trim(), limit: Number(limit) || 6 }],
        siteId: site,
        products: productId ? [String(productId)] : [],
      };
      const viewed = (Array.isArray(lastViewed) ? lastViewed : getLastViewed().split(','))
        .map((value) => String(value ?? '').trim())
        .filter(Boolean);
      if (viewed.length) body.lastViewed = viewed;

      const response = await fetch(`https://${site}.a.p13n.athoscommerce.net/v1/recommend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'omit',
        signal,
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(`Athos recommend failed with ${response.status}`);

      const data = await response.json();
      const results = Array.isArray(data) ? data[0]?.results || [] : [];
      return results.map(handleFromResult).filter(Boolean);
    };

    return { fetchCategoryPage, fetchSearchPage, fetchAutocomplete, fetchSuggest, fetchRecommendations };
  };

  /* Athos counts each inline banner as a result slot, so subtract them from totalResults.
     Undefined when there's no totalResults, so callers fall back to results.length. */
  const productResultCount = (data) => {
    const total = Number.parseInt(data?.pagination?.totalResults, 10);
    if (!Number.isFinite(total)) return undefined;
    const inline = data?.merchandising?.content?.inline;
    return Math.max(0, total - (Array.isArray(inline) ? inline.length : 0));
  };

  window.AthosClient = Object.freeze({
    createClient,
    normalizeProduct,
    normalizeField,
    handleFromResult,
    parseSortValue,
    decodeHtmlEntities,
  });
})();
